import { useEffect, useState } from 'react';
import * as Toast from '@radix-ui/react-toast';
import { CheckCircle2, X, AlertCircle } from 'lucide-react';
import { usePage } from '@inertiajs/react';

interface FlashProps {
  notice?: string;
  error?: string;
}

interface ToastProps {
  message: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  variant: 'success' | 'error';
}

const ToastMessage = ({ message, open, onOpenChange, variant }: ToastProps) => {
  const styles = {
    success: {
      bg: 'bg-green-50',
      icon: <CheckCircle2 className="h-5 w-5 text-green-400" aria-hidden="true" />,
      text: 'text-green-800',
      button:
        'bg-green-50 text-green-500 hover:bg-green-100 focus:ring-green-600 focus:ring-offset-green-50',
    },
    error: {
      bg: 'bg-red-50',
      icon: <AlertCircle className="h-5 w-5 text-red-400" aria-hidden="true" />,
      text: 'text-red-800',
      button: 'bg-red-50 text-red-500 hover:bg-red-100 focus:ring-red-600 focus:ring-offset-red-50',
    },
  };

  return (
    <Toast.Root
      className={`rounded-md ${styles[variant].bg} p-4 fixed bottom-4 right-4 shadow-lg`}
      open={open}
      onOpenChange={onOpenChange}
      duration={5000}
    >
      <div className="flex">
        <div className="shrink-0">{styles[variant].icon}</div>
        <div className="ml-3">
          <p className={`text-sm font-medium ${styles[variant].text}`}>{message}</p>
        </div>
        <div className="ml-auto pl-3">
          <div className="-mx-1.5 -my-1.5">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className={`inline-flex rounded-md p-1.5 focus:outline-none focus:ring-2 focus:ring-offset-2 ${styles[variant].button}`}
            >
              <span className="sr-only">Dismiss</span>
              <X className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>
    </Toast.Root>
  );
};

export function Flash({ notice, error }: FlashProps) {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [type, setType] = useState<'success' | 'error'>();
  const { flash } = usePage().props as { flash: FlashProps };

  useEffect(() => {
    const newNotice = notice || flash?.notice;
    const newError = error || flash?.error;

    if (newNotice || newError) {
      setMessage(newNotice || newError || '');
      setType(newNotice ? 'success' : 'error');
      setOpen(true);
    }
  }, [notice, error, flash]);

  return (
    <Toast.Provider swipeDirection="right">
      <ToastMessage
        message={message}
        open={open}
        onOpenChange={setOpen}
        variant={type || 'success'}
      />
      <Toast.Viewport />
    </Toast.Provider>
  );
}
